import React, { useState } from 'react';
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Button,
  Form,
  FormFeedback,
  Alert,
  Spinner,
} from 'reactstrap';
import ParticlesAuth from '../AuthenticationInner/ParticlesAuth';
import withRouter from '../../Components/Common/withRouter';
import { Link, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useUserContext } from '../../context/UserContext'; // Import UserContext
import { login } from '../../services/authService'; // Import authService
import logoLight from '../../assets/images/Mazaar Logo.png';
import { useDispatch } from 'react-redux';
import { isVisible } from 'slices/loader/reducer';
import { AccessLevels } from 'utility/interface';

// Define form input shape
interface LoginFormInputs {
  username: string;
  password: string;
}

// Yup validation schema
const loginSchema = Yup.object({
  username: Yup.string().required('Please enter your username'),
  password: Yup.string()
    .min(4, 'Password must be at least 4 characters')
    .required('Please enter your password'),
});

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { setUser, setToken } = useUserContext(); // Access context methods
  const [passwordShow, setPasswordShow] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInputs>({
    resolver: yupResolver(loginSchema) as any,
    defaultValues: {
      username: 'admin',
      password: '1234',
    },
  });

  const onSubmit = async (data: LoginFormInputs) => {
    setLoader(true);
    setError(null);
    dispatch(isVisible(true));
    try {
      const response = await login(data.username, data.password); // Call authService
      if (response?.data) {
        setUser(response.data); // Set user in context
        setToken(response.data); // Set token in context
        sessionStorage.setItem('authUser', JSON.stringify(response.data)); // Optional: store token in localStorage

        if (response.data.accessLevels?.includes(AccessLevels.SUPER_ADMIN)) {
          navigate('/organizations'); // Redirect to dashboard
        } else {
          navigate(`/organizations/${response.data.id}/properties`);
        }
      }
    } catch (err) {
      setError(`Login failed. Please check your credentials: ${err}`);
      setTimeout(() => setError(null), 3000); // Clear error after 3 seconds
    } finally {
      setLoader(false);
      dispatch(isVisible(false));
    }
  };

  document.title = 'MPM - Login';

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <div>
                    <Link to="/" className="d-inline-block auth-logo">
                      <img
                        src={logoLight}
                        alt=""
                        height="20"
                        style={{
                          height: '200px',
                          maxHeight: '200px',
                          width: 'auto',
                          objectFit: 'contain',
                          transform: 'scale(1.2)',
                          transformOrigin: 'center',
                        }}
                      />
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4 card-bg-fill">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">Welcome to MPM!</h5>
                      <p className="text-muted">Sign in to continue.</p>
                    </div>
                    {error && <Alert color="danger">{error}</Alert>}
                    <div className="p-2 mt-4">
                      <Form onSubmit={handleSubmit(onSubmit)}>
                        <div className="mb-3">
                          <Label htmlFor="email" className="form-label">
                            Username
                          </Label>
                          <Controller
                            name="username"
                            control={control}
                            render={({ field }) => (
                              <Input
                                {...field}
                                id="username"
                                className="form-control"
                                placeholder="Enter username"
                                type="text"
                                invalid={!!errors.username}
                              />
                            )}
                          />
                          {errors.username && (
                            <FormFeedback>
                              {errors.username.message}
                            </FormFeedback>
                          )}
                        </div>

                        <div className="mb-3">
                          <Label
                            className="form-label"
                            htmlFor="password-input"
                          >
                            Password
                          </Label>
                          <div className="position-relative auth-pass-inputgroup">
                            <Controller
                              name="password"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  {...field}
                                  id="password-input"
                                  className="form-control pe-5"
                                  placeholder="Enter password"
                                  type={passwordShow ? 'text' : 'password'}
                                  invalid={!!errors.password}
                                />
                              )}
                            />
                            <button
                              className="btn btn-link position-absolute end-0 top-0 text-decoration-none text-muted"
                              type="button"
                              onClick={() => setPasswordShow(!passwordShow)}
                            >
                              <i className="ri-eye-fill align-middle"></i>
                            </button>
                            {errors.password && (
                              <FormFeedback>
                                {errors.password.message}
                              </FormFeedback>
                            )}
                          </div>
                        </div>

                        <div className="mt-4">
                          <Button
                            color="success"
                            disabled={loader}
                            className="btn btn-success w-100"
                            type="submit"
                          >
                            {loader && (
                              <Spinner size="sm" className="me-2">
                                Loading...
                              </Spinner>
                            )}
                            Sign In
                          </Button>
                        </div>
                      </Form>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default withRouter(Login);
